import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { Role } from "~/shared.types";
import { safeStorage } from "~/shared/utils/safeStorage";

export interface UserStoreState {
  role: Role | null;
  token: string | null;
  hashedUserId: string | null;
  hashedUserIdExpiresAt: number | null;
  state: number | null;
  insurance: number | null;
  setHashedUserId: (hashedUserId: string | null) => void;
  setUserRole: (role: Role) => void;
  setToken: (token: string | null) => void;
  clearStore: () => void;
  setState: (state: number | null) => void;
  setInsurance: (insurance: number | null) => void;
}

export const useUserStore = create<UserStoreState>()(
  persist(
    (set) => ({
      role: null,
      token: null,
      hashedUserId: null,
      hashedUserIdExpiresAt: null,
      state: null,
      insurance: null,

      setState: (state: number | null) => {
        set(() => ({ state }));
      },

      setInsurance: (insurance: number | null) => {
        set(() => ({ insurance }));
      },

      setHashedUserId: (hashedUserId: string | null) => {
        const ttlSeconds = 1800;
        const expiresAt = hashedUserId ? Date.now() + ttlSeconds * 1000 : null;

        set(() => ({
          hashedUserId,
          hashedUserIdExpiresAt: expiresAt,
        }));
      },

      setUserRole: (role: Role | null) => {
        set(() => ({ role }));
      },

      setToken: (token: string | null) => {
        set(() => ({ token }));
      },

      clearStore: () => {
        set({
          role: null,
          token: null,
          hashedUserId: null,
          hashedUserIdExpiresAt: null,
          state: null,
          insurance: null,
        });
      },
    }),
    {
      name: "userStore",
      version: 1,
      storage: createJSONStorage(() => safeStorage),
      onRehydrateStorage:
        () => (state) => (set: (partial: Partial<UserStoreState>) => void) => {
          if (state?.hashedUserId && state?.hashedUserIdExpiresAt) {
            if (Date.now() > state.hashedUserIdExpiresAt) {
              set({
                hashedUserId: null,
                hashedUserIdExpiresAt: null,
              });
            }
          }
        },
    },
  ),
);
